import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';
import type {
  CompleteWorkflow,
  CreateWorkflowStatusInput,
  WorkflowChecklistItem,
  WorkflowStatus,
  WorkflowTransition,
  UnifiedCaseType,
} from '@leal-crm/shared';
import type { EmailTemplate, PostactionDefinition, PreconditionDefinition, WorkflowEditorWorkflowType } from './types';

type WorkflowPresetSummary = {
  id: string;
  name: string;
  description: string;
};
import { FALLBACK_EMAIL_TEMPLATES } from './constants';

const formatApiError = (err: unknown, fallback: string) => {
  if (err instanceof ApiError) {
    if (Array.isArray(err.details)) {
      const first = err.details[0] as { message?: string } | undefined;
      const detailMessage = first?.message ? ` (${first.message})` : '';
      return `${err.message}${detailMessage}`;
    }
    if (err.details && typeof err.details === 'object') {
      return `${err.message} (${JSON.stringify(err.details)})`;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : fallback;
};

export function useWorkflowEditorData(workflowType: WorkflowEditorWorkflowType, selectedType: UnifiedCaseType) {
  const { data: presets = [] } = useQuery({
    queryKey: ['admin', 'workflows', 'presets'],
    queryFn: () => api.get<WorkflowPresetSummary[]>('/api/admin/workflows/presets'),
  });
  const { data: workflows = [], isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'workflows'],
    queryFn: () => api.get<CompleteWorkflow[]>('/api/admin/workflows'),
  });

  const { data: preconditionDefs = [] } = useQuery({
    queryKey: ['admin', 'workflows', 'definitions', 'preconditions'],
    queryFn: () => api.get<PreconditionDefinition[]>('/api/admin/workflows/definitions/preconditions'),
  });

  const { data: postactionDefs = [] } = useQuery({
    queryKey: ['admin', 'workflows', 'definitions', 'postactions'],
    queryFn: () => api.get<PostactionDefinition[]>('/api/admin/workflows/definitions/postactions'),
  });

  const { data: emailTemplatesData } = useQuery({
    queryKey: ['admin', 'templates'],
    queryFn: async () => {
      try {
        return await api.get<EmailTemplate[]>('/api/admin/templates?include_inactive=false');
      } catch {
        return FALLBACK_EMAIL_TEMPLATES as EmailTemplate[];
      }
    },
  });
  const emailTemplates = (emailTemplatesData && emailTemplatesData.length > 0)
    ? emailTemplatesData
    : FALLBACK_EMAIL_TEMPLATES;

  const currentWorkflow = useMemo(() => {
    if (workflowType === 'job') {
      const typedWorkflow = workflows.find(
        (w) => w.workflow.workflowType === 'job' && w.workflow.bookingType === selectedType
      );
      return typedWorkflow || workflows.find((w) => w.workflow.workflowType === 'job');
    }
    return workflows.find(
      (w) => w.workflow.workflowType === 'booking' && w.workflow.bookingType === selectedType
    );
  }, [workflows, workflowType, selectedType]);

  const { data: checklistItems = [] } = useQuery({
    queryKey: ['admin', 'workflows', currentWorkflow?.workflow.id, 'checklist-items'],
    queryFn: () =>
      api.get<WorkflowChecklistItem[]>(`/api/admin/workflows/by-id/${currentWorkflow?.workflow.id}/checklist-items`),
    enabled: !!currentWorkflow?.workflow.id,
  });

  return {
    workflows,
    presets,
    preconditionDefs,
    postactionDefs,
    emailTemplates,
    currentWorkflow,
    checklistItems,
    isLoading,
    isError,
    error,
  };
}

export function useResetWorkflows(options: {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ presetId }: { presetId: string }) => {
      return await api.post('/api/admin/workflows/reset', { presetId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows', 'presets'] });
      options.onSuccess('Flujos restablecidos a valores por defecto');
    },
    onError: (err) => {
      options.onError(formatApiError(err, 'No se pudo restablecer los flujos'));
    },
  });
}

export function useWorkflowEditorMutations(options: {
  workflowId?: string;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onDeleteError?: (error: ApiError) => void;
}) {
  const { workflowId, onSuccess, onError, onDeleteError } = options;
  const queryClient = useQueryClient();

  const cloneWorkflowMutation = useMutation({
    mutationFn: ({ sourceWorkflowId, bookingType }: { sourceWorkflowId: string; bookingType: UnifiedCaseType }) =>
      api.post(`/api/admin/workflows/by-id/${sourceWorkflowId}/clone`, { bookingType }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows'] });
      onSuccess('Flujo creado');
    },
    onError: (err) => {
      onError(formatApiError(err, 'Error al crear flujo'));
    },
  });

  const createStatusMutation = useMutation({
    mutationFn: ({
      workflowId: targetWorkflowId,
      data,
    }: {
      workflowId: string;
      data: CreateWorkflowStatusInput;
    }) => api.post(`/api/admin/workflows/by-id/${targetWorkflowId}/statuses`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows'] });
      onSuccess('Estado creado');
    },
    onError: (err) => {
      onError(formatApiError(err, 'Error al crear estado'));
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({
      workflowId: targetWorkflowId,
      statusId,
      updates,
    }: {
      workflowId: string;
      statusId: string;
      updates: Partial<WorkflowStatus>;
    }) => api.put(`/api/admin/workflows/by-id/${targetWorkflowId}/statuses/${statusId}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows'] });
      onSuccess('Estado actualizado');
    },
    onError: (err) => {
      onError(formatApiError(err, 'Error al actualizar estado'));
    },
  });

  const deleteStatusMutation = useMutation({
    mutationFn: ({
      workflowId: targetWorkflowId,
      statusId,
      fallbackToUndefined,
    }: {
      workflowId: string;
      statusId: string;
      fallbackToUndefined?: boolean;
    }) =>
      api.delete(`/api/admin/workflows/by-id/${targetWorkflowId}/statuses/${statusId}`, {
        fallbackToUndefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows'] });
      onSuccess('Estado eliminado');
    },
    onError: (err) => {
      if (err instanceof ApiError && onDeleteError) {
        onDeleteError(err);
        return;
      }
      onError(formatApiError(err, 'No se pudo eliminar el estado'));
    },
  });

  const updateTransitionMutation = useMutation({
    mutationFn: ({
      workflowId: targetWorkflowId,
      transitionId,
      updates,
    }: {
      workflowId: string;
      transitionId: string;
      updates: Partial<WorkflowTransition>;
    }) => api.put(`/api/admin/workflows/by-id/${targetWorkflowId}/transitions/${transitionId}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows'] });
      onSuccess('Transicion actualizada');
    },
    onError: (err) => {
      onError(formatApiError(err, 'Error al actualizar transicion'));
    },
  });

  const createTransitionMutation = useMutation({
    mutationFn: ({
      workflowId: targetWorkflowId,
      fromStatusId,
      toStatusId,
    }: {
      workflowId: string;
      fromStatusId: string;
      toStatusId: string;
    }) =>
      api.post(`/api/admin/workflows/by-id/${targetWorkflowId}/transitions`, {
        fromStatusId,
        toStatusId,
        isEnabled: true,
        dialogType: 'none',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows'] });
      onSuccess('Transicion creada');
    },
    onError: (err) => {
      onError(formatApiError(err, 'Error al crear transicion'));
    },
  });

  const createChecklistItemMutation = useMutation({
    mutationFn: ({
      workflowId: targetWorkflowId,
      data,
    }: {
      workflowId: string;
      data: Omit<WorkflowChecklistItem, 'id' | 'workflowId' | 'createdAt' | 'updatedAt'>;
    }) => api.post(`/api/admin/workflows/by-id/${targetWorkflowId}/checklist-items`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows', workflowId, 'checklist-items'] });
      onSuccess('Requisito agregado');
    },
    onError: (err) => {
      onError(formatApiError(err, 'Error al agregar requisito'));
    },
  });

  const updateChecklistItemMutation = useMutation({
    mutationFn: ({
      workflowId: targetWorkflowId,
      itemId,
      updates,
    }: {
      workflowId: string;
      itemId: string;
      updates: Partial<WorkflowChecklistItem>;
    }) => api.put(`/api/admin/workflows/by-id/${targetWorkflowId}/checklist-items/${itemId}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows', workflowId, 'checklist-items'] });
      onSuccess('Requisito actualizado');
    },
    onError: (err) => {
      onError(formatApiError(err, 'Error al actualizar requisito'));
    },
  });

  const deleteChecklistItemMutation = useMutation({
    mutationFn: ({
      workflowId: targetWorkflowId,
      itemId,
    }: {
      workflowId: string;
      itemId: string;
    }) => api.delete(`/api/admin/workflows/by-id/${targetWorkflowId}/checklist-items/${itemId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'workflows', workflowId, 'checklist-items'] });
      onSuccess('Requisito eliminado');
    },
    onError: (err) => {
      onError(formatApiError(err, 'Error al eliminar requisito'));
    },
  });

  return {
    cloneWorkflowMutation,
    createStatusMutation,
    updateStatusMutation,
    deleteStatusMutation,
    updateTransitionMutation,
    createTransitionMutation,
    createChecklistItemMutation,
    updateChecklistItemMutation,
    deleteChecklistItemMutation,
  };
}
